import type { PresentationLocale } from "../slides/types";

const TEMPLATE_BASE_PATH = "/presentation/source";
const TEMPLATE_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

const templateCache = new Map<string, string>();

type HtmlSlideOptions = {
  responsive?: boolean;
};

function injectBasePatchStyles(html: string, responsive = false) {
  const responsiveCss = responsive
    ? `
  @media (max-width: 1280px) {
    body { overflow: hidden !important; }
    .slide-container {
      width: 100vw !important;
      height: 100vh !important;
      transform: scale(min(100vw / 1280, 100vh / 720));
      transform-origin: top left;
    }
  }
  @media (max-width: 768px) {
    .slide-container { transform: scale(min(100vw / 1280, 100vh / 720)); }
  }`
    : "";

  const patchCss = `
<style id="d4-base-patch">
  .d4-brand-logo { width: 22px; height: 22px; object-fit: contain; filter: drop-shadow(0 0 6px rgba(0, 196, 180, 0.28)); }
  .d4-avatar-photo { width: 100%; height: 100%; object-fit: cover; border-radius: 12px; }
  .d4-link, .d4-link:visited { color: inherit; text-decoration: none; }
  .d4-link:hover { color: #00c4b4; }
  .d4-social-chips { display:flex; flex-wrap:wrap; gap:8px; margin-top: 14px; }
  .d4-social-chips a { font-family:'JetBrains Mono', monospace; font-size:11px; color:#cfd7dd; text-decoration:none; border:1px solid #333; padding:5px 8px; border-radius:6px; background:#111; }
  ${responsiveCss}
</style>`;

  return html.replace("</head>", `${patchCss}</head>`);
}

function patchLogoAndPhoto(html: string) {
  let patched = html;

  patched = patched.replace(
    /<span class="font-bold text-accent text-xs">D4<\/span>/g,
    '<img class="d4-brand-logo" src="/brand/logo-symbol.png" alt="D4" />'
  );

  patched = patched.replace(
    /<span class="font-bold text-\[#00c4b4\] text-xs">D4<\/span>/g,
    '<img class="d4-brand-logo" src="/brand/logo-symbol.png" alt="D4" />'
  );

  patched = patched.replace(
    /<span class="font-bold text-\[#00c4b4\] text-lg">D4<\/span>/g,
    '<img class="d4-brand-logo" src="/brand/logo-symbol.png" alt="D4" />'
  );

  patched = patched.replace(
    /<span class="font-mono font-bold">DB<\/span>/,
    '<img class="d4-avatar-photo" src="/about/dario-portrait-9x16.webp" alt="Dario Marco Bellini"/>'
  );

  patched = patched.replace(
    /<button class="cta-button">([\s\S]*?)<\/button>/,
    '<a class="cta-button d4-link" href="https://tidycal.com/dimensione-4-di-dariomarcobellini/meeting-conoscitivo-30min" target="_blank" rel="noreferrer">$1</a>'
  );

  patched = patched.replace(
    /<p class="contact-value">www\.dimensione4\.it<\/p>/,
    '<p class="contact-value"><a class="d4-link" href="https://dimensione4.it" target="_blank" rel="noreferrer">www.dimensione4.it</a></p>'
  );

  patched = patched.replace(
    /<p class="contact-value">linkedin\.com\/in\/dariobellini<\/p>/,
    '<p class="contact-value"><a class="d4-link" href="https://linkedin.com/in/dariobellini" target="_blank" rel="noreferrer">linkedin.com/in/dariobellini</a></p>'
  );

  patched = patched.replace(
    /<p class="contact-value">dariomarcobellini@dimensione4\.it<\/p>/,
    '<p class="contact-value"><a class="d4-link" href="mailto:dariomarcobellini@dimensione4.it">dariomarcobellini@dimensione4.it</a></p>'
  );

  return patched;
}

function patchLanguage(html: string, locale: PresentationLocale) {
  if (locale === "it") return html;

  let t = html;
  // Helper: global replace without regex (no escaping needed)
  const r = (from: string, to: string) => {
    t = t.split(from).join(to);
  };

  // --- HTML lang attribute ---
  r('lang="it"', 'lang="en"');

  // --- Slide 01 - Cover ---
  r(
    'Full Stack <span class="font-bold text-[#00c4b4]">Developer Freelance</span>',
    'Full Stack <span class="font-bold text-[#00c4b4]">Freelance Developer</span>'
  );

  // --- Slide 02 - Chi Sono ---
  r("<title>Chi sono - Dario Marco Bellini</title>", "<title>About Me - Dario Marco Bellini</title>");
  r("PAGE 02 // CHI SONO", "PAGE 02 // ABOUT ME");
  r(
    'Sviluppatore con solida esperienza enterprise in <strong class="text-white">Accenture</strong> (2021-2026).',
    'Developer with solid enterprise experience at <strong class="text-white">Accenture</strong> (2021-2026).'
  );
  r(
    'Dal 2026 opero come freelance e fondatore di <strong class="text-accent">Dimensione 4</strong>, trasformando idee complesse in prodotti digitali scalabili.',
    'Since 2026 I work as a freelancer and founder of <strong class="text-accent">Dimensione 4</strong>, transforming complex ideas into scalable digital products.'
  );
  r("Specializzazioni", "Specializations");
  r("Architetture Modulari", "Modular Architectures");
  r("AI Applicata (RAG System)", "Applied AI (RAG System)");
  r("Frontend Accessibile", "Accessible Frontend");
  r("Cosa Offro", "What I Offer");
  r(
    "Sviluppo rapido di prodotti validati dal mercato con focus su scalabilità.",
    "Rapid development of market-validated products with a focus on scalability."
  );
  r("Assistenti AI Custom", "Custom AI Assistants");
  r(
    "Integrazione di LLM e Knowledge Base proprietarie per automazione processi.",
    "Integration of LLMs and proprietary Knowledge Bases for process automation."
  );
  r("Integrazioni IoT", "IoT Integrations");
  r(
    "Dal firmware ESP32 alla dashboard cloud per monitoraggio real-time.",
    "From ESP32 firmware to cloud dashboard for real-time monitoring."
  );
  r(
    "Design system coerenti e interfacce fluide orientate all'utente.",
    "Consistent design systems and fluid, user-centered interfaces."
  );

  // --- Slide 03 - Timeline Accenture ---
  r("<title>Esperienza Accenture - Timeline</title>", "<title>Accenture Experience - Timeline</title>");
  r(
    'Esperienza <span class="text-accent">Accenture</span>',
    'Accenture <span class="text-accent">Experience</span>'
  );
  r("TIMELINE PROGETTI 2021 → 2026", "PROJECT TIMELINE 2021 → 2026");
  r("Ottimizzazione rendering 3D e nuvole di punti massive.", "3D rendering optimization and massive point cloud processing.");
  r("App industriali per tablet rugged Windows/Android.", "Industrial apps for Windows/Android rugged tablets.");
  r("Assistente RAG production-ready su documenti tecnici.", "Production-ready RAG assistant on technical documents.");
  r("Sistema end-to-end: dispositivo, cloud e dashboard.", "End-to-end system: device, cloud, and dashboard.");
  r("Sistemi KPI, compliance e generazione documentale.", "KPI systems, compliance, and document generation.");
  r("Anni Esperienza", "Years Experience");
  r(">Ambiente<", ">Environment<");
  r(">Approccio<", ">Approach<");
  r(">Con:<", ">With:<");

  // --- Slide 04 - Progetti Enterprise ---
  r(
    "<title>Progetti - Digital Twin &amp; Saipem CIW</title>",
    "<title>Projects - Digital Twin &amp; Saipem CIW</title>"
  );
  r(
    'Progetti <span class="text-accent">Enterprise</span>',
    'Enterprise <span class="text-accent">Projects</span>'
  );
  r('<strong class="text-white block mb-1">La Sfida</strong>', '<strong class="text-white block mb-1">The Challenge</strong>');
  r(
    "Gestione e rendering di nuvole di punti massive (point cloud) via browser con performance stabili.",
    "Managing and rendering massive point clouds (point cloud) in-browser with stable performance."
  );
  r('<strong class="text-white block mb-1">Soluzione Tecnica</strong>', '<strong class="text-white block mb-1">Technical Solution</strong>');
  r(
    "Integrazione Three.js e Potree con caricamento dinamico (LOD) e lazy loading degli asset 3D.",
    "Three.js and Potree integration with dynamic loading (LOD) and lazy loading of 3D assets."
  );
  r('<strong class="text-white block mb-1">Risultati</strong>', '<strong class="text-white block mb-1">Results</strong>');
  r(
    "FPS stabili su hardware standard e UX fluida per navigazione complessa di asset industriali.",
    "Stable FPS on standard hardware and fluid UX for complex navigation of industrial assets."
  );
  r('<strong class="text-white block mb-1">Contesto</strong>', '<strong class="text-white block mb-1">Context</strong>');
  r(
    "Applicazioni critiche per tablet rugged (Windows/Android) utilizzati in cantieri remoti.",
    "Critical applications for rugged tablets (Windows/Android) used in remote construction sites."
  );
  r(
    '<strong class="text-white block mb-1">Architettura</strong>',
    '<strong class="text-white block mb-1">Architecture</strong>'
  );
  r(
    "Sistema ibrido con C# .NET e Xamarin. Database distribuito Couchbase per sync offline/online.",
    "Hybrid system with C# .NET and Xamarin. Distributed Couchbase database for offline/online sync."
  );
  r(
    "Sincronizzazione dati affidabile in aree a bassa connettività e logging centralizzato su Graylog.",
    "Reliable data synchronization in low-connectivity areas and centralized logging on Graylog."
  );

  // --- Slide 05 - Progetti Innovativi ---
  r(
    "<title>Progetti - Vigili del Fuoco AI &amp; IoT</title>",
    "<title>Projects - Fire Department AI &amp; IoT</title>"
  );
  r(
    'Progetti <span class="text-accent">Innovativi</span>',
    'Innovative <span class="text-accent">Projects</span>'
  );
  r(
    "Implementazione RAG (Retrieval-Augmented Generation) per risposte contestuali su base documentale tecnica.",
    "RAG (Retrieval-Augmented Generation) implementation for contextual responses on technical document bases."
  );
  r(
    "FastAPI per orchestrazione LLM. Containerizzazione Docker per deploy scalabile e riproducibile.",
    "FastAPI for LLM orchestration. Docker containerization for scalable and reproducible deployments."
  );
  r(
    "Embeddings vettoriali su PostgreSQL (pgvector) e caching Redis per ottimizzazione query.",
    "Vector embeddings on PostgreSQL (pgvector) and Redis caching for query optimization."
  );
  r(
    "Firmware per ESP32-S3 con comunicazione BLE ottimizzata per basso consumo energetico.",
    "Firmware for ESP32-S3 with BLE communication optimized for low energy consumption."
  );
  r(
    "Ingestione dati ad alta frequenza con FastAPI e archiviazione time-series su TimescaleDB.",
    "High-frequency data ingestion with FastAPI and time-series storage on TimescaleDB."
  );
  r('<strong class="text-white block mb-1">Scalabilità</strong>', '<strong class="text-white block mb-1">Scalability</strong>');
  r(
    "Orchestrazione Kubernetes per microservizi e MinIO per object storage distribuito.",
    "Kubernetes orchestration for microservices and MinIO for distributed object storage."
  );

  // --- Slide 06 - FinTech ---
  r(
    'Progetto <span class="text-accent">FinTech</span>',
    'FinTech <span class="text-accent">Project</span>'
  );
  r("PAGAMENTI DIGITALI &amp; COMPLIANCE", "DIGITAL PAYMENTS &amp; COMPLIANCE");
  r("Il Contesto", "The Context");
  r(
    "Inserito in un contesto enterprise critico per i pagamenti digitali italiani. La necessità primaria era gestire enormi volumi di dati per reportistica di compliance e generazione massiva di documenti PDF/Excel per la Pubblica Amministrazione.",
    "Operating in a critical enterprise context for Italian digital payments. The primary need was to handle massive data volumes for compliance reporting and bulk generation of PDF/Excel documents for Public Administration."
  );
  r("Attività Chiave", "Key Activities");
  r(
    'Sviluppo backend in <strong class="text-white">Java Spring Boot</strong> per API RESTful.',
    'Backend development in <strong class="text-white">Java Spring Boot</strong> for RESTful APIs.'
  );
  r(
    'Ottimizzazione query complesse su <strong class="text-white">PostgreSQL</strong>.',
    'Complex query optimization on <strong class="text-white">PostgreSQL</strong>.'
  );
  r(
    'Containerizzazione servizi con <strong class="text-white">Docker</strong>.',
    'Service containerization with <strong class="text-white">Docker</strong>.'
  );
  r("Risultati &amp; Impatto", "Results &amp; Impact");
  r("Reporting Automatizzato", "Automated Reporting");
  r("Generazione Massiva", "Bulk Generation");
  r(
    '"Implementazione di pipeline robuste per la generazione documentale, riducendo i tempi di esecuzione e garantendo la consistenza dei dati finanziari."',
    '"Implementation of robust pipelines for document generation, reducing execution times and ensuring consistency of financial data."'
  );

  // --- Slide 07 - Stack Tecnologico ---
  r("<title>Stack Tecnologico - Dario Marco Bellini</title>", "<title>Tech Stack - Dario Marco Bellini</title>");
  r('Stack <span class="text-accent">Tecnologico</span>', 'Tech <span class="text-accent">Stack</span>');

  // --- Slide 08 - Servizi & Metodo ---
  r("<title>Dimensione 4 - Servizi &amp; Metodo</title>", "<title>Dimensione 4 - Services &amp; Method</title>");
  r("SERVIZI &amp; METODO", "SERVICES &amp; METHOD");
  r(
    "Siti web ottimizzati per Core Web Vitals e SEO tecnico. Performance al primo posto.",
    "Websites optimized for Core Web Vitals and technical SEO. Performance first."
  );
  r(
    "Dall'idea al prodotto funzionante. Validazione rapida e architettura scalabile.",
    "From idea to working product. Rapid validation and scalable architecture."
  );
  r(
    "Interfacce accessibili, fluide e coerenti. Design system rigorosi.",
    "Accessible, fluid, and consistent interfaces. Rigorous design systems."
  );
  r(
    "Motion design e micro-interazioni che rendono il brand memorabile.",
    "Motion design and micro-interactions that make the brand memorable."
  );
  // Method steps - longer strings first to avoid partial-match conflicts
  r("Analisi problemi e contesto", "Problem and context analysis");
  r("Priorità e timeline realistica", "Priorities and realistic timeline");
  r("Definizione chiara dello scope", "Clear scope definition");
  r("Sviluppo iterativo PR-ready", "Iterative PR-ready development");
  r("Test automatizzati inclusi", "Automated tests included");
  r("Documentazione e deploy guide", "Documentation and deploy guides");
  r("Monitoraggio metriche post-lancio", "Post-launch metrics monitoring");
  r("Miglioramenti continui (data-driven)", "Continuous improvements (data-driven)");
  r("Supporto attivo a lungo termine", "Long-term active support");
  r(">MAPPA <", ">MAP <");
  r(">Analisi<", ">Analysis<");
  r(">COSTRUISCE <", ">BUILD <");
  r(">EVOLVI <", ">EVOLVE <");
  r(">Crescita<", ">Growth<");
  // Footer quality bar
  r("Performance Prioritaria", "Performance First");
  r("Architettura Pulita", "Clean Architecture");
  r("Miglioramenti Misurabili", "Measurable Improvements");

  // --- Slide 09 - Contatti ---
  r("<title>Contatti - Dario Marco Bellini</title>", "<title>Contact - Dario Marco Bellini</title>");
  r("Disponibile per nuovi progetti", "Available for new projects");
  r(
    'Parliamo del tuo <br/> <span class="text-accent">Progetto</span>',
    "Let's talk about your <br/> <span class=\"text-accent\">Project</span>"
  );
  r(
    "Dal concept alla produzione scalabile. Trasformo idee complesse in soluzioni digitali concrete e performanti.",
    "From concept to scalable production. I turn complex ideas into concrete, high-performance digital solutions."
  );
  r("Prenota una call (30 min)", "Book a call (30 min)");
  r(">Scrivimi<", ">Email<");
  r(">Esplora<", ">Explore<");
  r(">Connettiti<", ">Connect<");
  r("Priorità chiare", "Clear priorities");
  r("Esecuzione rapida", "Fast execution");
  r("Risultati concreti", "Concrete results");

  return t;
}

async function fetchTemplate(slideId: number) {
  const cacheKey = `slide-${slideId}`;
  if (templateCache.has(cacheKey)) {
    return templateCache.get(cacheKey) as string;
  }

  const path = `${TEMPLATE_BASE_PATH}/slide-${String(slideId).padStart(2, "0")}.html`;
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Template not found: ${path}`);
  }

  const html = await response.text();
  templateCache.set(cacheKey, html);
  return html;
}

export async function getPresentationHtmlSlides(
  locale: PresentationLocale,
  slideIds?: number[],
  options: HtmlSlideOptions = {}
): Promise<string[]> {
  const selectedIds = (slideIds?.length ? slideIds : TEMPLATE_IDS).filter((id) => TEMPLATE_IDS.includes(id as never));
  const raw = await Promise.all(selectedIds.map((slideId) => fetchTemplate(slideId)));

  return raw.map((html) => {
    const withBrand = patchLogoAndPhoto(html);
    const localized = patchLanguage(withBrand, locale);
    return injectBasePatchStyles(localized, options.responsive ?? false);
  });
}
